import {
  type CSSProperties,
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  adjustLabelTimingsForIntro,
  buildFrameLabelCSS,
  buildFrameLabelTimings,
} from "../core/labels";
import {
  normalizeFrames,
  extractValue,
  extractItemConfig,
} from "../core/normalize";
import { calculateTotalDuration } from "../core/durations";
import {
  applySequentialIntro,
  applySequentialTransitions,
  buildKeyframeTimeline,
  TimelineEntry,
} from "../core/timelines";
import { isLightColor } from "../core/color";
import {
  FRAME_LABEL_VERTICAL_SPACE,
  KEY_LABEL_VERTICAL_SPACE,
  defaultHold,
  defaultOptions,
} from "../core/constants";
import { Frame } from "../types";
import { useAnimator, AnimatorHandle } from "../hooks/useAnimator";

let idCounter = 0;

const EASING_PRESETS: Record<string, [number, number, number, number]> = {
  ease: [0.25, 0.1, 0.25, 1],
  "ease-in": [0.42, 0, 1, 1],
  "ease-out": [0, 0, 0.58, 1],
  "ease-in-out": [0.42, 0, 0.58, 1],
};

const createCubicBezier = (
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
) => {
  if (p1x === p1y && p2x === p2y) {
    return (t: number) => t;
  }

  const cx = 3 * p1x;
  const bx = 3 * (p2x - p1x) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * p1y;
  const by = 3 * (p2y - p1y) - cy;
  const ay = 1 - cy - by;

  const sampleCurveX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleCurveY = (t: number) => ((ay * t + by) * t + cy) * t;
  const sampleCurveDerivativeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;

  const solveCurveX = (x: number) => {
    let t = x;
    for (let i = 0; i < 8; i += 1) {
      const x2 = sampleCurveX(t) - x;
      if (Math.abs(x2) < 1e-6) return t;
      const d2 = sampleCurveDerivativeX(t);
      if (Math.abs(d2) < 1e-6) break;
      t -= x2 / d2;
    }

    let t0 = 0;
    let t1 = 1;
    t = x;
    while (t0 < t1) {
      const x2 = sampleCurveX(t);
      if (Math.abs(x2 - x) < 1e-6) return t;
      if (x > x2) {
        t0 = t;
      } else {
        t1 = t;
      }
      t = (t0 + t1) / 2;
    }
    return t;
  };

  return (x: number) => sampleCurveY(solveCurveX(x));
};

const countDecimals = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  const text = Math.abs(value).toString();
  if (!text.includes("e")) {
    const [, fraction = ""] = text.split(".");
    return fraction.length;
  }

  const [coeff, exp] = text.split("e");
  const exponent = Number.parseInt(exp, 10);
  const fractionLength = (coeff.split(".")[1] || "").length;
  if (Number.isNaN(exponent)) return fractionLength;
  return Math.max(0, fractionLength - exponent);
};

const ABBREVIATIONS = [
  { value: 1e12, suffix: "T" },
  { value: 1e9, suffix: "B" },
  { value: 1e6, suffix: "M" },
  { value: 1e3, suffix: "k" },
];

const roundToSignificant = (value: number, digits: number) => {
  if (!Number.isFinite(value)) return 0;
  if (value === 0) return 0;
  const abs = Math.abs(value);
  const scale = 10 ** (digits - 1 - Math.floor(Math.log10(abs)));
  return Math.round(value * scale) / scale;
};

const resolveEasing = (easing: string) => {
  const normalized = easing?.trim() || "linear";
  if (normalized === "linear") {
    return (t: number) => t;
  }

  const preset = EASING_PRESETS[normalized];
  if (preset) {
    return createCubicBezier(...preset);
  }

  const match = normalized.match(/cubic-bezier\(([^)]+)\)/i);
  if (match) {
    const parts = match[1]
      .split(",")
      .map((value) => Number.parseFloat(value.trim()));
    if (parts.length === 4 && parts.every((value) => Number.isFinite(value))) {
      return createCubicBezier(parts[0], parts[1], parts[2], parts[3]);
    }
  }

  return (t: number) => t;
};

const buildValueTimeline = (
  values: number[],
  holdStart: number,
  holdEnd: number,
  duration: number,
  advanceDelay: number,
): TimelineEntry[] => {
  const frameCount = values.length;
  if (frameCount === 0) return [];

  const safeValues = values.map((value) =>
    Number.isFinite(value) ? value : 0,
  );
  const entries: TimelineEntry[] = [];
  const startHold = Math.max(0, Math.min(holdStart, 100));
  const endHold = Math.max(0, Math.min(holdEnd, 100 - startHold));
  const startHoldMs = (startHold / 100) * duration;
  const endHoldMs = (endHold / 100) * duration;
  const animatedDuration = Math.max(0, duration - startHoldMs - endHoldMs);
  const stepTime = frameCount > 1 ? animatedDuration / (frameCount - 1) : 0;
  const pause = Math.max(0, advanceDelay);
  const totalDuration = calculateTotalDuration(
    duration,
    advanceDelay,
    frameCount,
  );
  const toPercent = (timeMs: number) => (timeMs / totalDuration) * 100;

  const firstValue = safeValues[0];
  entries.push({ percent: 0, height: firstValue });
  if (startHoldMs > 0) {
    entries.push({ percent: toPercent(startHoldMs), height: firstValue });
  }

  let currentTime = startHoldMs;
  for (let index = 1; index < frameCount; index += 1) {
    const prevValue = safeValues[index - 1];
    if (pause > 0) {
      currentTime += pause;
      entries.push({ percent: toPercent(currentTime), height: prevValue });
    }
    currentTime += stepTime;
    entries.push({
      percent: toPercent(currentTime),
      height: safeValues[index],
    });
  }

  const lastValue = safeValues[frameCount - 1];
  if (endHoldMs > 0) {
    const endHoldStart = totalDuration - endHoldMs;
    entries.push({ percent: toPercent(endHoldStart), height: lastValue });
    entries.push({ percent: 100, height: lastValue });
  }

  if (!entries.some((entry) => entry.percent === 0)) {
    entries.unshift({ percent: 0, height: firstValue });
  }
  if (!entries.some((entry) => entry.percent === 100)) {
    entries.push({ percent: 100, height: lastValue });
  }

  return entries.filter(
    (entry, index, arr) =>
      index === 0 || entry.percent !== arr[index - 1].percent,
  );
};

const interpolateTimeline = (
  timeline: TimelineEntry[],
  percent: number,
  easingFn: (t: number) => number,
) => {
  if (!timeline.length) return 0;

  const clamped = Math.max(0, Math.min(100, percent));
  if (clamped <= timeline[0].percent) return timeline[0].height;

  for (let i = 1; i < timeline.length; i += 1) {
    const prev = timeline[i - 1];
    const next = timeline[i];
    if (clamped <= next.percent) {
      const span = next.percent - prev.percent;
      if (span <= 0) return next.height;
      const local = (clamped - prev.percent) / span;
      const eased = easingFn(local);
      return prev.height + (next.height - prev.height) * eased;
    }
  }

  return timeline[timeline.length - 1].height;
};

const getIterationProgress = (
  elapsedMs: number,
  delayMs: number,
  iterationDuration: number,
  iterations: number | "infinite",
  loopMode: "normal" | "alternate",
) => {
  if (elapsedMs < delayMs) return 0;
  const localElapsed = elapsedMs - delayMs;
  if (iterationDuration <= 0) return 1;

  const totalIterations =
    typeof iterations === "number" ? Math.max(0, iterations) : Infinity;
  const totalMs = iterationDuration * totalIterations;
  if (Number.isFinite(totalMs) && localElapsed >= totalMs) {
    const finalIteration = Math.max(0, totalIterations - 1);
    const isReverse = loopMode === "alternate" && finalIteration % 2 === 1;
    return isReverse ? 0 : 1;
  }

  const iterationIndex = Math.floor(localElapsed / iterationDuration);
  let progress = (localElapsed % iterationDuration) / iterationDuration;
  if (loopMode === "alternate" && iterationIndex % 2 === 1) {
    progress = 1 - progress;
  }
  return progress;
};

const formatValueLabel = (value: number, finalValue: number) => {
  if (!Number.isFinite(value)) return "--";

  const cappedDecimals = countDecimals(finalValue);
  const absValue = Math.abs(value);
  let suffix = "";
  let scaled = value;

  for (const abbrev of ABBREVIATIONS) {
    if (absValue >= abbrev.value) {
      suffix = abbrev.suffix;
      scaled = value / abbrev.value;
      break;
    }
  }

  let rounded = roundToSignificant(scaled, 3);
  const absRounded = Math.abs(rounded);

  if (absRounded >= 1000 && suffix) {
    const currentIndex = ABBREVIATIONS.findIndex(
      (item) => item.suffix === suffix,
    );
    if (currentIndex > 0) {
      const next = ABBREVIATIONS[currentIndex - 1];
      suffix = next.suffix;
      rounded = roundToSignificant(value / next.value, 3);
    }
  }

  const digits = absRounded >= 1 ? Math.floor(Math.log10(absRounded)) + 1 : 0;
  const requiredDecimals = absRounded >= 1 ? Math.max(0, 3 - digits) : 3;
  const decimals = Math.min(requiredDecimals, cappedDecimals);
  const formatted = Math.abs(rounded).toFixed(decimals);
  const sign = rounded < 0 ? "-" : "";
  return `${sign}${formatted}${suffix}`;
};

export interface BubbleChartAnimatorProps {
  frames: Frame[];
  width?: number;
  height?: number;
  padding?: number;
  duration?: number;
  advanceDelay?: number;
  easing?: string;
  loopMode?: "normal" | "alternate";
  iterations?: number | "infinite";
  stagger?: number;
  labels?: boolean;
  labelFontSize?: number;
  valueLabels?: boolean;
  animateEach?: boolean;
  hold?: { start?: number; end?: number };
  color?: string;
  className?: string;
  style?: CSSProperties;
  autoPlay?: boolean;
  introPercentOverride?: number;
}

export const BubbleChartAnimator = forwardRef(function BubbleChartAnimator(
  props: BubbleChartAnimatorProps,
  ref: ForwardedRef<AnimatorHandle>,
) {
  const settings = useMemo(
    () => ({
      ...defaultOptions,
      ...props,
      chartType: "bubble",
      hold: { ...defaultOptions.hold, ...(props.hold || {}) },
    }),
    [props],
  );

  const {
    frames,
    width,
    height,
    padding,
    duration,
    advanceDelay,
    easing,
    loopMode,
    iterations,
    stagger,
    labels,
    labelFontSize: rawLabelFontSize,
    valueLabels,
    animateEach,
    hold,
    color = "#6c5ce7",
    className,
    style,
    autoPlay = true,
    introPercentOverride,
  } = settings;
  const labelFontSize = Number.isFinite(rawLabelFontSize)
    ? rawLabelFontSize
    : defaultOptions.labelFontSize;
  const valueLabelFontSize = labelFontSize;
  const valueLabelOffset = Math.round(valueLabelFontSize * 0.35);
  const minValueLabelRadius = Math.max(8, Math.round(valueLabelFontSize * 0.6));

  const holdStart = hold?.start ?? defaultHold.start;
  const holdEnd = hold?.end ?? defaultHold.end;

  const animationIdRef = useRef(`ds-bubble-${++idCounter}`);
  const baseName = animationIdRef.current;
  const { paused } = useAnimator(ref, autoPlay);
  const valueLabelRefs = useRef<(SVGTextElement | null)[]>([]);
  const firstBubbleRef = useRef<SVGCircleElement | null>(null);

  if (!Array.isArray(frames) || frames.length === 0) {
    throw new Error("frames must be a non-empty array");
  }

  const { keys, globalMin, globalMax } = useMemo(
    () => normalizeFrames(frames),
    [frames],
  );
  const valuesByKey = useMemo(() => {
    return keys.reduce<Record<string, number[]>>((acc, key) => {
      acc[key] = frames.map((frame) => extractValue(frame[key]));
      return acc;
    }, {});
  }, [frames, keys]);

  // Extract per-item configurations for each key across all frames
  const itemConfigsByKey = useMemo(() => {
    return keys.reduce<
      Record<string, Array<ReturnType<typeof extractItemConfig>>>
    >((acc, key) => {
      acc[key] = frames.map((frame) => extractItemConfig(frame[key]));
      return acc;
    }, {});
  }, [frames, keys]);

  // Calculate value label styles per item (accounting for per-item colors)
  const valueLabelStylesByKey = useMemo(() => {
    return keys.reduce<
      Record<string, { fill: string; stroke: string; isLight: boolean }>
    >((acc, key) => {
      // Use the first frame's item config to determine color, or fallback to global
      const itemConfig = itemConfigsByKey[key]?.[0];
      const itemColor = itemConfig?.color || color;
      const isLight = isLightColor(itemColor);
      acc[key] = {
        fill: isLight ? "#111" : "#fff",
        stroke: isLight ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.35)",
        isLight,
      };
      return acc;
    }, {});
  }, [keys, itemConfigsByKey, color]);

  const labelTimings = useMemo(
    () =>
      buildFrameLabelTimings(
        frames,
        holdStart,
        holdEnd,
        duration,
        advanceDelay,
      ),
    [frames, holdEnd, holdStart, duration, advanceDelay],
  );
  const frameLabelSpace =
    labelTimings.length > 0
      ? Math.max(FRAME_LABEL_VERTICAL_SPACE, Math.round(labelFontSize * 2))
      : 0;
  const keyLabelSpace = labels
    ? Math.max(KEY_LABEL_VERTICAL_SPACE, Math.round(labelFontSize + 8))
    : 0;
  const chartHeight = Math.max(0, height - frameLabelSpace - keyLabelSpace);
  const keyLabelY = chartHeight + labelFontSize;
  const availableWidth = width - padding * 2;
  const gap = keys.length > 0 ? (availableWidth * 0.12) / (keys.length + 1) : 0;
  const bubbleSpace =
    keys.length > 0
      ? (availableWidth - gap * (keys.length + 1)) / keys.length
      : availableWidth;
  const radiusCap = Math.min(bubbleSpace / 2, (chartHeight - padding * 2) / 2);
  const getBubblePosition = useCallback(
    (timelineHeight: number) => {
      const radius = Math.min(Math.max(4, timelineHeight / 2), radiusCap);
      const baseline = chartHeight - padding - radius;
      const float = Math.min(radiusCap, timelineHeight / 3);
      const y = Math.max(padding + radius, baseline - float);
      return { radius, y };
    },
    [chartHeight, padding, radiusCap],
  );

  const introPercent = useMemo(() => {
    if (typeof introPercentOverride === "number") return introPercentOverride;
    if (animateEach && keys.length > 0) {
      return Math.min(40, Math.max(12, keys.length * 24));
    }
    return 0;
  }, [animateEach, introPercentOverride, keys.length]);

  const easingFn = useMemo(() => resolveEasing(easing), [easing]);

  const bubbleTimelines = useMemo(() => {
    return keys.map((key, index) => {
      let timeline = buildKeyframeTimeline(
        valuesByKey[key],
        globalMin,
        globalMax,
        radiusCap * 2,
        0,
        holdStart,
        holdEnd,
        duration,
        advanceDelay,
      );

      if (introPercent > 0) {
        timeline = applySequentialIntro(
          timeline,
          index,
          keys.length,
          introPercent,
        );
      }
      if (animateEach) {
        timeline = applySequentialTransitions(
          timeline,
          index,
          keys.length,
          introPercent,
        );
      }

      return timeline;
    });
  }, [
    advanceDelay,
    animateEach,
    duration,
    globalMax,
    globalMin,
    holdEnd,
    holdStart,
    introPercent,
    keys,
    radiusCap,
    valuesByKey,
  ]);

  const valueTimelines = useMemo(() => {
    if (!valueLabels) return [];
    return keys.map((key, index) => {
      let timeline = buildValueTimeline(
        valuesByKey[key],
        holdStart,
        holdEnd,
        duration,
        advanceDelay,
      );

      if (introPercent > 0) {
        timeline = applySequentialIntro(
          timeline,
          index,
          keys.length,
          introPercent,
        );
      }
      if (animateEach) {
        timeline = applySequentialTransitions(
          timeline,
          index,
          keys.length,
          introPercent,
        );
      }

      return timeline;
    });
  }, [
    advanceDelay,
    animateEach,
    duration,
    holdEnd,
    holdStart,
    introPercent,
    keys,
    valueLabels,
    valuesByKey,
  ]);

  const finalValues = useMemo(
    () =>
      keys.map((key) => {
        const values = valuesByKey[key] ?? [];
        const lastValue = values[values.length - 1];
        return Number.isFinite(lastValue) ? lastValue : 0;
      }),
    [keys, valuesByKey],
  );

  const baseTotalDuration = calculateTotalDuration(
    duration,
    advanceDelay,
    frames.length,
  );
  const totalDuration =
    introPercent > 0
      ? Math.round(baseTotalDuration / ((100 - introPercent) / 100))
      : baseTotalDuration;
  const effectiveIterations =
    typeof iterations === "number" ? iterations : "infinite";

  const bubbleKeyframes = useMemo(() => {
    return bubbleTimelines
      .map((timeline, index) => {
        const keyframes = timeline
          .map(({ percent, height: timelineHeight }) => {
            const radius = Math.min(Math.max(4, timelineHeight / 2), radiusCap);
            const baseline = chartHeight - padding - radius;
            const float = Math.min(radiusCap, timelineHeight / 3);
            const bubbleY = Math.max(padding + radius, baseline - float);
            return `${percent.toFixed(2)}% { r: ${radius.toFixed(2)}px; cy: ${bubbleY.toFixed(2)}px; }`;
          })
          .join("\n");
        return `@keyframes ${baseName}-bubble-${index} {\n${keyframes}\n}`;
      })
      .join("\n");
  }, [baseName, bubbleTimelines, chartHeight, padding, radiusCap]);

  const effectiveLabelTimings =
    introPercent > 0
      ? adjustLabelTimingsForIntro(labelTimings, introPercent)
      : labelTimings;
  const labelCSS = useMemo(
    () => buildFrameLabelCSS(baseName, effectiveLabelTimings),
    [baseName, effectiveLabelTimings],
  );
  const maxDelay = Math.max(0, stagger * (keys.length - 1));

  useEffect(() => {
    if (!valueLabels) return;
    if (paused) {
      return;
    }

    let rafId = 0;
    const step = () => {
      // Get elapsed time from the first bubble's CSS animation to stay in sync
      const bubble = firstBubbleRef.current;
      let elapsed = 0;

      if (bubble) {
        const animations = bubble.getAnimations();
        const bubbleAnimation = animations.find(
          (a) => a instanceof CSSAnimation,
        );
        if (bubbleAnimation && bubbleAnimation.currentTime != null) {
          elapsed = bubbleAnimation.currentTime as number;
        }
      }

      for (let index = 0; index < keys.length; index += 1) {
        const label = valueLabelRefs.current[index];
        if (!label) continue;

        // Adjust elapsed for stagger offset of this bubble relative to first bubble
        const bubbleElapsed = elapsed + stagger * index;
        const progress = getIterationProgress(
          bubbleElapsed,
          stagger * index,
          totalDuration,
          effectiveIterations,
          loopMode,
        );
        const percent = progress * 100;
        const height = interpolateTimeline(
          bubbleTimelines[index] ?? [],
          percent,
          easingFn,
        );
        const value = interpolateTimeline(
          valueTimelines[index] ?? [],
          percent,
          easingFn,
        );
        const { radius, y: bubbleY } = getBubblePosition(height);
        const y = bubbleY + valueLabelOffset;
        const isZero = Math.abs(value) < 1e-6;
        const tooSmall = radius < minValueLabelRadius;

        if (isZero || tooSmall) {
          if (label.textContent) {
            label.textContent = "";
          }
          if (label.getAttribute("opacity") !== "0") {
            label.setAttribute("opacity", "0");
          }
        } else {
          const text = formatValueLabel(value, finalValues[index] ?? value);
          if (label.textContent !== text) {
            label.textContent = text;
          }
          if (label.getAttribute("opacity") !== "1") {
            label.setAttribute("opacity", "1");
          }
        }
        label.setAttribute("y", y.toFixed(2));
      }

      if (typeof effectiveIterations === "number") {
        const totalMs =
          totalDuration * Math.max(0, effectiveIterations) + maxDelay;
        if (elapsed >= totalMs) return;
      }

      rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);

    return () => cancelAnimationFrame(rafId);
  }, [
    bubbleTimelines,
    easingFn,
    effectiveIterations,
    finalValues,
    getBubblePosition,
    keys.length,
    loopMode,
    maxDelay,
    minValueLabelRadius,
    paused,
    stagger,
    totalDuration,
    valueLabelOffset,
    valueLabels,
    valueTimelines,
  ]);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Animated bubble chart"
      className={className}
      style={{ overflow: "visible", color, ...style }}
    >
      <defs>
        {/* Create striped patterns for each unique color and dataItemStyle */}
        {keys.map((key, index) => {
          const itemConfig = itemConfigsByKey[key]?.[0];
          const itemStyle = itemConfig?.dataItemStyle;
          const itemColor = itemConfig?.color || color;

          if (itemStyle === "striped") {
            return (
              <pattern
                key={`${key}-stripe`}
                id={`${baseName}-bubble-stripe-${index}`}
                patternUnits="userSpaceOnUse"
                width="32"
                height="32"
                patternTransform="rotate(45)"
              >
                <rect width="16" height="32" fill={itemColor} />
                <rect
                  x="16"
                  width="16"
                  height="32"
                  fill={itemColor}
                  opacity="0.75"
                />
              </pattern>
            );
          }
          return null;
        })}
      </defs>
      <style>{`${bubbleKeyframes}\n${labelCSS}`}</style>
      <g transform={`translate(0, ${frameLabelSpace})`}>
        <g transform={`translate(${padding}, 0)`}>
          {keys.map((key, index) => {
            const timeline = bubbleTimelines[index] ?? [];
            const firstHeight = timeline[0]?.height ?? 0;
            const radius = Math.min(Math.max(4, firstHeight / 2), radiusCap);
            const baseline = chartHeight - padding - radius;
            const float = Math.min(radiusCap, firstHeight / 3);
            const bubbleY = Math.max(padding + radius, baseline - float);
            const x = gap * (index + 1) + bubbleSpace * index + bubbleSpace / 2;
            const delay = stagger * index;

            // Get per-item configuration (use first frame's config)
            const itemConfig = itemConfigsByKey[key]?.[0];
            const itemStyle = itemConfig?.dataItemStyle;
            const itemColor = itemConfig?.color || color;
            const fillValue =
              itemStyle === "striped"
                ? `url(#${baseName}-bubble-stripe-${index})`
                : itemColor;

            return (
              <circle
                key={key}
                ref={index === 0 ? firstBubbleRef : undefined}
                cx={x.toFixed(2)}
                cy={bubbleY.toFixed(2)}
                r={radius.toFixed(2)}
                fill={fillValue}
                style={{
                  filter: "drop-shadow(0 10px 16px rgba(0,0,0,0.35))",
                  animationName: `${baseName}-bubble-${index}`,
                  animationDuration: `${totalDuration}ms`,
                  animationTimingFunction: easing,
                  animationDelay: `${delay}ms`,
                  animationIterationCount: effectiveIterations,
                  animationDirection: loopMode,
                  animationFillMode: "forwards",
                  animationPlayState: paused ? "paused" : "running",
                  transformOrigin: `${x.toFixed(2)}px ${(chartHeight - padding).toFixed(2)}px`,
                }}
              />
            );
          })}
          {valueLabels ? (
            <g>
              {keys.map((key, index) => {
                const timeline = bubbleTimelines[index] ?? [];
                const firstHeight = timeline[0]?.height ?? 0;
                const { radius, y: bubbleY } = getBubblePosition(firstHeight);
                const y = bubbleY + valueLabelOffset;
                const x =
                  gap * (index + 1) + bubbleSpace * index + bubbleSpace / 2;
                const initialValue =
                  valueTimelines[index]?.[0]?.height ??
                  valuesByKey[key]?.[0] ??
                  0;
                const finalValue = finalValues[index] ?? initialValue;
                const initialHidden =
                  Math.abs(initialValue) < 1e-6 || radius < minValueLabelRadius;

                // Get per-item valueLabel configuration and styles
                const itemConfig = itemConfigsByKey[key]?.[0];
                const showValueLabel = itemConfig?.valueLabels ?? valueLabels;
                const labelStyles = valueLabelStylesByKey[key];

                if (!showValueLabel) return null;

                return (
                  <text
                    key={`${key}-value`}
                    ref={(node) => {
                      valueLabelRefs.current[index] = node;
                    }}
                    x={x.toFixed(2)}
                    y={y.toFixed(2)}
                    textAnchor="middle"
                    fontSize={valueLabelFontSize}
                    fontWeight={600}
                    fill={labelStyles.fill}
                    stroke={labelStyles.stroke}
                    strokeWidth={2}
                    paintOrder="stroke"
                    dominantBaseline="middle"
                    opacity={initialHidden ? 0 : 1}
                  >
                    {initialHidden
                      ? ""
                      : formatValueLabel(initialValue, finalValue)}
                  </text>
                );
              })}
            </g>
          ) : null}
          {labels ? (
            <g>
              {keys.map((key, index) => {
                const x =
                  gap * (index + 1) + bubbleSpace * index + bubbleSpace / 2;
                const y = keyLabelY;
                return (
                  <text
                    key={`${key}-label`}
                    x={x.toFixed(2)}
                    y={y.toFixed(2)}
                    textAnchor="middle"
                    fontSize={labelFontSize}
                    fill="currentColor"
                  >
                    {key}
                  </text>
                );
              })}
            </g>
          ) : null}
        </g>
      </g>

      {effectiveLabelTimings.map(({ label }, index) => (
        <text
          key={`frame-label-${index}`}
          x={padding.toFixed(2)}
          y={(padding + labelFontSize).toFixed(2)}
          textAnchor="start"
          fontSize={labelFontSize}
          fontWeight={700}
          fill="currentColor"
          style={{
            animationName: `${baseName}-label-${index}`,
            animationDuration: `${totalDuration}ms`,
            animationTimingFunction: "steps(1, end)",
            animationDelay: "0ms",
            animationIterationCount: effectiveIterations,
            animationDirection: loopMode,
            animationFillMode: "both",
            animationPlayState: paused ? "paused" : "running",
          }}
        >
          {label}
        </text>
      ))}
    </svg>
  );
});

export default BubbleChartAnimator;
